// ============================================================================
// PhoneField.jsx — shared country-code phone input (E.164)
// Location: ClientApp/shared/PhoneField.jsx
// Forms Streamlining build (5 Aug 2026)
//
// antd-compatible controlled component (value / onChange) for use inside
// Form.Item. Stores E.164 (+64221545721); displays a country selector
// (flag + dial code, searchable) with the national number formatted as the
// user types (libphonenumber-js AsYouType).
//
// Behaviour:
//   • Default country NZ; priority list NZ, AU, FJ, IN, then A–Z.
//   • Typing "021 234 5678" with NZ selected → emits +64212345678.
//   • Pasting "+679 922 3300" auto-switches the selector to Fiji.
//   • Legacy values without a country code parse against the selected
//     country, so old drafts still display.
//   • Country Select follows the house AntD pattern: getPopupContainer on
//     the parent + dropdown zIndex 9999 so it works inside Drawer/Modal.
//
// Pair with rules.phoneValid (+ rules.required where mandatory) from
// ../shared/formRules.
//
// Developed & Architected by Sachin Singh — E-Business
// www.ebusinesserp.com  sachin@ebusinesserp.com
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import { Input, Select } from 'antd';
import {
    AsYouType, getCountries, getCountryCallingCode, parsePhoneNumberFromString,
} from 'libphonenumber-js';

const PRIORITY = ['NZ', 'AU', 'FJ', 'IN'];

const regionNames = (() => {
    try { return new Intl.DisplayNames(['en'], { type: 'region' }); } catch { return null; }
})();
const nameOf = (iso) => {
    try { return (regionNames && regionNames.of(iso)) || iso; } catch { return iso; }
};
const flagOf = (iso) => iso.replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

const COUNTRIES = (() => {
    const all = getCountries();
    const head = PRIORITY.filter((c) => all.includes(c));
    const rest = all
        .filter((c) => !PRIORITY.includes(c))
        .sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
    return [...head, ...rest].map((iso) => ({
        iso,
        code: getCountryCallingCode(iso),
        name: nameOf(iso),
        flag: flagOf(iso),
    }));
})();

export default function PhoneField({
    value, onChange, defaultCountry = 'NZ', disabled = false,
    size, placeholder = 'e.g. 021 123 4567', autoComplete = 'tel',
}) {
    const [country, setCountry] = useState(defaultCountry);
    const [text, setText] = useState('');
    const emitted = useRef('');   // last value WE sent up — breaks sync loops

    const emit = (e164) => {
        emitted.current = e164 || '';
        if (onChange) onChange(e164 || undefined);
    };

    // Sync DOWN from the form (initial load / setFieldsValue / draft restore).
    useEffect(() => {
        const v = (value == null ? '' : String(value)).trim();
        if (v === emitted.current) return;              // our own echo
        if (!v) { setText(''); emitted.current = ''; return; }
        const parsed = parsePhoneNumberFromString(v)
            || parsePhoneNumberFromString(v, country)
            || parsePhoneNumberFromString(v, defaultCountry);
        if (parsed) {
            if (parsed.country && parsed.country !== country) setCountry(parsed.country);
            setText(parsed.formatNational());
            emitted.current = v;
        } else {
            setText(v);                                  // unparseable legacy — show as-is
            emitted.current = v;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const composeE164 = (iso, raw) => {
        const trimmed = (raw || '').trim();
        if (!trimmed) return '';
        if (trimmed.startsWith('+')) {
            const p = parsePhoneNumberFromString(trimmed);
            return p ? p.number : trimmed.replace(/[^\d+]/g, '');
        }
        const ayt = new AsYouType(iso);
        ayt.input(trimmed);
        const num = ayt.getNumber();
        if (num) return num.number;
        const digits = trimmed.replace(/\D/g, '').replace(/^0+/, '');
        return digits ? `+${getCountryCallingCode(iso)}${digits}` : '';
    };

    const handleText = (e) => {
        const raw = e.target.value;
        if (raw.trim().startsWith('+')) {
            // International paste/typing — may switch country.
            const p = parsePhoneNumberFromString(raw.trim());
            if (p && p.country && p.country !== country) setCountry(p.country);
            setText(p ? p.formatInternational() : raw);
            emit(composeE164(country, raw));
            return;
        }
        const formatted = new AsYouType(country).input(raw);
        setText(formatted);
        emit(composeE164(country, raw));
    };

    const handleCountry = (iso) => {
        setCountry(iso);
        // Re-emit the current national digits under the new country code.
        const national = text.trim().startsWith('+')
            ? (parsePhoneNumberFromString(text.trim())?.nationalNumber || '')
            : text;
        if (national) {
            const formatted = new AsYouType(iso).input(String(national));
            setText(formatted);
        }
        emit(composeE164(iso, String(national || '')));
    };

    return (
        <Input
            value={text}
            onChange={handleText}
            disabled={disabled}
            size={size}
            placeholder={placeholder}
            autoComplete={autoComplete}
            inputMode="tel"
            addonBefore={(
                <Select
                    value={country}
                    onChange={handleCountry}
                    disabled={disabled}
                    showSearch
                    optionLabelProp="label"
                    filterOption={(input, opt) =>
                        (opt?.title || '').toLowerCase().includes(input.toLowerCase())}
                    getPopupContainer={(t) => t.parentNode}
                    dropdownStyle={{ zIndex: 9999, minWidth: 260 }}
                    style={{ width: 96 }}
                >
                    {COUNTRIES.map((c) => (
                        <Select.Option
                            key={c.iso}
                            value={c.iso}
                            title={`${c.name} +${c.code} ${c.iso}`}
                            label={`${c.flag} +${c.code}`}
                        >
                            {c.flag} {c.name} <span style={{ color: '#8c8c8c' }}>+{c.code}</span>
                        </Select.Option>
                    ))}
                </Select>
            )}
        />
    );
}
